import { useState } from "react";
import { api, downloadUrl, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Loader2, FileText, Download, Eye, ImageIcon } from "lucide-react";
import BuyCreditsButton from "../components/BuyCreditsButton";

const EMPTY = {
  personal: { full_name: "", title: "", email: "", phone: "", location: "", website: "", linkedin: "", photo: "" },
  summary: "",
  experience: [],
  education: [],
  skills: [],
  languages: [],
  certifications: [],
};

const TEMPLATES = [
  { id: "classic",   labelKey: "cv.tpl.classic",   desc: "Serif Bordeaux",                  hasPhoto: false },
  { id: "modern",    labelKey: "cv.tpl.modern",    desc: "Two-column · Photo · Navy",       hasPhoto: true  },
  { id: "minimal",   labelKey: "cv.tpl.minimal",   desc: "ATS-friendly · Black & white",    hasPhoto: false },
  { id: "dark",      labelKey: "cv.tpl.dark",      desc: "Dark sidebar · Amber accent",     hasPhoto: true  },
  { id: "ats_bold",  labelKey: "cv.tpl.atsBold",   desc: "ATS · Bold name · Timeline dots", hasPhoto: false },
  { id: "ats_clean", labelKey: "cv.tpl.atsClean",  desc: "ATS · Centered · Pipe contact",   hasPhoto: false },
];

export default function CvBuilder() {
  const { user, refresh } = useAuth();
  const t = useT();
  const [data, setData] = useState(EMPTY);
  const [template, setTemplate] = useState("classic");
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadName, setUploadName] = useState("");

  const patchPersonal = (k, v) => setData(d => ({ ...d, personal: { ...d.personal, [k]: v }}));
  const currentTpl = TEMPLATES.find(x => x.id === template);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadName(file.name);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/cv/extract", fd, { headers: {"Content-Type":"multipart/form-data"}});
      setData(normalize(r.data));
      toast.success(t("toast.parsed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); }
  };

  const onPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo must be under 3 MB");
    const reader = new FileReader();
    reader.onload = () => patchPersonal("photo", reader.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if ((user?.credits ?? 0) < 1) return toast.error(t("toast.needCredits"));
    setGenerating(true); setResult(null);
    try {
      const r = await api.post("/cv/generate-pdf", { data, template });
      setResult(r.data);
      toast.success(t("toast.cvGenerated"));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF generation failed");
    } finally { setGenerating(false); }
  };

  return (
    <div className="paper-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cv-builder-page">

        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="pill mb-3">{t("cv.badge")}</div>
            <h1 className="font-serif-display text-4xl font-bold">{t("cv.title")}</h1>
            <p className="text-[#475569] mt-1">{t("cv.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${BACKEND_URL}/api/cv/sample-pdf?template=${template}`} target="_blank" rel="noreferrer" data-testid="cv-sample-preview-link">
              <Button variant="outline" className="border-[#E2DBCE]"><Eye size={14} className="mr-1"/> {t("cv.sample")}</Button>
            </a>
            <span className="pill">{user?.credits ?? 0} {t("common.credits")}</span>
            <BuyCreditsButton onSuccess={refresh}/>
          </div>
        </div>

        {/* Template picker */}
        <div className="card-classic p-4 mb-6" data-testid="template-picker">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-jb text-xs uppercase tracking-wider text-[#64748B]">{t("cv.template")}</div>
            <span className="pill">{TEMPLATES.length} options</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setTemplate(tpl.id)}
                data-testid={`template-option-${tpl.id}`}
                className={`text-left rtl:text-right p-3 rounded-md border transition-all ${
                  template === tpl.id
                    ? "border-[#0F172A] bg-[#0F172A] text-white shadow-md"
                    : "border-[#E2DBCE] bg-white hover:border-[#0F172A]"
                }`}
              >
                <div className="font-serif-display text-lg font-semibold">{t(tpl.labelKey)}</div>
                <div className={`text-xs mt-1 ${template === tpl.id ? "text-[#DBEAFE]" : "text-[#64748B]"}`}>{tpl.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <label
              className="card-classic p-6 border-dashed border-2 border-[#E2DBCE] bg-white flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0F172A]"
              data-testid="cv-upload-dropzone"
            >
              {extracting ? (
                <>
                  <Loader2 size={28} className="text-[#881337] animate-spin"/>
                  <div className="mt-3 font-serif-display text-lg">{t("cv.extracting")}</div>
                  <div className="text-sm text-[#64748B]">{uploadName}</div>
                </>
              ) : (
                <>
                  <Upload size={26} className="text-[#0F172A]"/>
                  <div className="mt-2 font-serif-display text-lg">{t("cv.uploadTitle")}</div>
                  <div className="text-xs text-[#64748B]">{t("cv.uploadSub")}</div>
                </>
              )}
              <input type="file" className="hidden" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp"
                onChange={onUpload} data-testid="cv-file-upload-input"/>
            </label>

            <Section title={t("cv.section.personal")} testId="cv-section-personal">
              {currentTpl?.hasPhoto && (
                <div className="mb-4 flex items-center gap-4 p-3 rounded-md bg-[#F4F0EA] border border-[#E2DBCE]">
                  {data.personal.photo ? (
                    <img src={data.personal.photo} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" data-testid="cv-photo-preview"/>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white border-2 border-dashed border-[#CBD5E1] grid place-items-center text-[#64748B]">
                      <ImageIcon size={20}/>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#0F172A]">Profile photo</div>
                    <div className="text-xs text-[#64748B] mb-2">Optional · PNG/JPG · under 3 MB · shown on Modern & Dark templates</div>
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={onPhotoUpload} data-testid="cv-photo-input"/>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#0F172A] text-white hover:bg-[#1E293B]">
                          <Upload size={12}/> {data.personal.photo ? "Change" : "Upload photo"}
                        </span>
                      </label>
                      {data.personal.photo && (
                        <button type="button" onClick={() => patchPersonal("photo", "")} className="text-xs text-[#DC2626] underline" data-testid="cv-photo-remove">
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("cv.field.fullName")} value={data.personal.full_name} onChange={v=>patchPersonal("full_name", v)} testId="cv-input-full-name"/>
                <Field label={t("cv.field.jobTitle")} value={data.personal.title} onChange={v=>patchPersonal("title", v)} testId="cv-input-title"/>
                <Field label={t("cv.field.email")} value={data.personal.email} onChange={v=>patchPersonal("email", v)} testId="cv-input-email"/>
                <Field label={t("cv.field.phone")} value={data.personal.phone} onChange={v=>patchPersonal("phone", v)} testId="cv-input-phone"/>
                <Field label={t("cv.field.location")} value={data.personal.location} onChange={v=>patchPersonal("location", v)} testId="cv-input-location"/>
                <Field label={t("cv.field.website")} value={data.personal.website} onChange={v=>patchPersonal("website", v)} testId="cv-input-website"/>
              </div>
            </Section>

            <Section title={t("cv.section.summary")} testId="cv-section-summary">
              <Textarea rows={4} value={data.summary} onChange={e=>setData(d=>({...d, summary:e.target.value}))} data-testid="cv-input-summary"/>
            </Section>

            <Section title={t("cv.section.experience")} testId="cv-section-experience"
              action={<AddButton onClick={() => setData(d => ({...d, experience:[...d.experience, {company:"",role:"",location:"",start:"",end:"",bullets:[""]}]}))} testId="cv-add-experience"/>}>
              {data.experience.map((e, i) => (
                <RepeaterRow key={i} onDelete={() => setData(d => ({...d, experience: d.experience.filter((_,ix)=>ix!==i)}))} testId={`cv-experience-${i}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("cv.field.role")} value={e.role} onChange={v=>updateAt(setData, "experience", i, "role", v)}/>
                    <Field label={t("cv.field.company")} value={e.company} onChange={v=>updateAt(setData, "experience", i, "company", v)}/>
                    <Field label={t("cv.field.location")} value={e.location} onChange={v=>updateAt(setData, "experience", i, "location", v)}/>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label={t("cv.field.start")} value={e.start} onChange={v=>updateAt(setData, "experience", i, "start", v)}/>
                      <Field label={t("cv.field.end")} value={e.end} onChange={v=>updateAt(setData, "experience", i, "end", v)}/>
                    </div>
                  </div>
                  <Label className="mt-3 block">{t("cv.bullets")}</Label>
                  {(e.bullets || []).map((b, bi) => (
                    <div key={bi} className="flex gap-2 mt-2">
                      <Input value={b} onChange={ev => {
                        const bullets=[...e.bullets]; bullets[bi]=ev.target.value;
                        updateAt(setData, "experience", i, "bullets", bullets);
                      }}/>
                      <Button variant="ghost" size="icon" onClick={() => {
                        const bullets=e.bullets.filter((_,ix)=>ix!==bi);
                        updateAt(setData, "experience", i, "bullets", bullets);
                      }}><Trash2 size={14}/></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="mt-2 border-[#E2DBCE]" onClick={() => {
                    updateAt(setData, "experience", i, "bullets", [...(e.bullets||[]), ""]);
                  }}><Plus size={14} className="mr-1"/> {t("cv.addBullet")}</Button>
                </RepeaterRow>
              ))}
            </Section>

            <Section title={t("cv.section.education")} testId="cv-section-education"
              action={<AddButton onClick={() => setData(d => ({...d, education:[...d.education, {institution:"",degree:"",field:"",start:"",end:"",location:""}]}))} testId="cv-add-education"/>}>
              {data.education.map((e, i) => (
                <RepeaterRow key={i} onDelete={() => setData(d => ({...d, education: d.education.filter((_,ix)=>ix!==i)}))} testId={`cv-education-${i}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("cv.field.institution")} value={e.institution} onChange={v=>updateAt(setData, "education", i, "institution", v)}/>
                    <Field label={t("cv.field.degree")} value={e.degree} onChange={v=>updateAt(setData, "education", i, "degree", v)}/>
                    <Field label={t("cv.field.fieldOfStudy")} value={e.field} onChange={v=>updateAt(setData, "education", i, "field", v)}/>
                    <Field label={t("cv.field.location")} value={e.location} onChange={v=>updateAt(setData, "education", i, "location", v)}/>
                    <Field label={t("cv.field.start")} value={e.start} onChange={v=>updateAt(setData, "education", i, "start", v)}/>
                    <Field label={t("cv.field.end")} value={e.end} onChange={v=>updateAt(setData, "education", i, "end", v)}/>
                  </div>
                </RepeaterRow>
              ))}
            </Section>

            <Section title={t("cv.section.skills")} testId="cv-section-skills">
              <Input value={(data.skills || []).join(", ")}
                onChange={e => setData(d => ({...d, skills: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}))}
                placeholder={t("cv.skillsPh")}
                data-testid="cv-input-skills"/>
            </Section>

            <Section title={t("cv.section.languages")} testId="cv-section-languages">
              <Input value={(data.languages || []).join(", ")}
                onChange={e => setData(d => ({...d, languages: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}))}
                placeholder={t("cv.langsPh")}
                data-testid="cv-input-languages"/>
            </Section>
          </div>

          {/* Right column: actions + live preview matching selected template */}
          <div className="lg:col-span-5">
            <div className="sticky top-24 space-y-4">
              <div className="card-classic p-5" data-testid="cv-editor-form">
                <div className="flex items-center justify-between">
                  <div className="font-mono-jb text-xs uppercase tracking-wider text-[#64748B]">{t("cv.template")}</div>
                  <span className="pill" data-testid={`template-current-${template}`}>{t(currentTpl?.labelKey || "cv.tpl.classic")}</span>
                </div>
                <Button
                  onClick={generate}
                  disabled={generating}
                  className="w-full mt-3 bg-[#0F172A] hover:bg-[#1E293B]"
                  data-testid="download-classic-cv-pdf-button"
                >
                  {generating ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("cv.generating")}</>) : (<><FileText size={16} className="mr-2"/> {t("cv.generate")}</>)}
                </Button>
                {result && (
                  <a href={downloadUrl(result.download_url)} target="_blank" rel="noreferrer" className="block mt-3">
                    <Button variant="outline" className="w-full border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white" data-testid="cv-download-result">
                      <Download size={14} className="mr-2"/> {t("common.download")} {result.title}.pdf
                    </Button>
                  </a>
                )}
              </div>

              <div className="card-classic p-3" dir="ltr">
                <div className="flex items-center justify-between mb-2 px-2">
                  <div className="font-mono-jb text-xs uppercase tracking-wider text-[#64748B]">{t("cv.livePreview")}</div>
                  <span className="pill">{template}</span>
                </div>
                <CvPreview data={data} template={template} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
function Section({ title, children, action, testId }) {
  return (
    <div className="card-classic p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif-display text-xl font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#64748B] font-mono-jb">{label}</Label>
      <Input value={value || ""} onChange={(e)=>onChange(e.target.value)} data-testid={testId} className="mt-1"/>
    </div>
  );
}
function AddButton({ onClick, testId }) {
  return (
    <Button size="sm" variant="outline" className="border-[#E2DBCE]" onClick={onClick} data-testid={testId}>
      <Plus size={14} className="mr-1"/>
    </Button>
  );
}
function RepeaterRow({ children, onDelete, testId }) {
  return (
    <div className="border-t border-[#F4F0EA] pt-3 mt-3 first:mt-0 first:pt-0 first:border-t-0" data-testid={testId}>
      <div className="flex justify-end mb-2">
        <Button size="sm" variant="ghost" onClick={onDelete}><Trash2 size={14} className="text-[#DC2626]"/></Button>
      </div>
      {children}
    </div>
  );
}
function updateAt(setter, key, idx, field, value) {
  setter(d => {
    const list=[...d[key]]; list[idx]={...list[idx], [field]: value};
    return {...d, [key]: list};
  });
}
function normalize(d) {
  const base = JSON.parse(JSON.stringify(EMPTY));
  if (!d) return base;
  base.personal = { ...base.personal, ...(d.personal || {}) };
  base.summary = d.summary || "";
  base.experience = (d.experience || []).map(e => ({
    company: e.company||"", role: e.role||"", location: e.location||"",
    start: e.start||"", end: e.end||"", bullets: e.bullets || [],
  }));
  base.education = (d.education || []).map(e => ({
    institution: e.institution||"", degree: e.degree||"", field: e.field||"",
    location: e.location||"", start: e.start||"", end: e.end||"",
  }));
  base.skills = d.skills || [];
  base.languages = d.languages || [];
  base.certifications = d.certifications || [];
  return base;
}

/* ---------- Live Preview (per template) ---------- */
function CvPreview({ data, template }) {
  if (template === "modern") return <ModernPreview data={data}/>;
  if (template === "minimal") return <MinimalPreview data={data}/>;
  if (template === "dark") return <DarkPreview data={data}/>;
  if (template === "ats_bold") return <AtsBoldPreview data={data}/>;
  if (template === "ats_clean") return <AtsCleanPreview data={data}/>;
  return <ClassicPreview data={data}/>;
}

function AtsBoldPreview({ data }) {
  const p = data.personal || {};
  const contact = [p.email, p.phone, p.location, p.website, p.linkedin].filter(Boolean).join(" · ");
  const dates = (e) => (e.start || e.end) ? `${e.start||""} – ${e.end||"Present"}` : "";
  return (
    <div className="rounded-md border border-[#E2DBCE] bg-white p-4 text-[10.5px] leading-4 text-black min-h-[600px]"
      data-testid="cv-preview-ats_bold" style={{fontFamily:"Plus Jakarta Sans"}}>
      <div className="text-2xl font-bold text-center">{p.full_name || "Your Name"}</div>
      {p.title && <div className="text-[#333] text-[11px] text-center">{p.title}</div>}
      {contact && <div className="text-[10px] text-[#333] text-center mt-1">{contact}</div>}
      {data.summary && (<><AtsHdr>PROFILE</AtsHdr><p>{data.summary}</p></>)}
      {data.experience?.length > 0 && (<>
        <AtsHdr>WORK EXPERIENCE</AtsHdr>
        {data.experience.map((e,i)=>(
          <div key={i} className="mt-1">
            <div className="flex justify-between gap-2">
              <div className="font-semibold flex-1"><span className="mr-1">●</span>{e.role || "Role"}{e.company && ` · ${e.company}`}{e.location && ` · ${e.location}`}</div>
              <div className="text-[#666] whitespace-nowrap">{dates(e)}</div>
            </div>
            {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi} className="ml-3">• {b}</div>)}
          </div>
        ))}
      </>)}
      {data.education?.length > 0 && (<>
        <AtsHdr>EDUCATION AND TRAINING</AtsHdr>
        {data.education.map((e,i)=>(
          <div key={i} className="mt-1">
            <div className="flex justify-between gap-2">
              <div className="font-semibold flex-1"><span className="mr-1">●</span>{e.degree}{e.field && ` in ${e.field}`}{e.institution && ` · ${e.institution}`}</div>
              <div className="text-[#666] whitespace-nowrap">{dates(e)}</div>
            </div>
            {e.location && <div className="text-[#666] ml-3">{e.location}</div>}
          </div>
        ))}
      </>)}
      {(data.skills?.length > 0 || data.languages?.length > 0) && (
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            {data.skills?.length > 0 && (<>
              <AtsHdr>SKILLS</AtsHdr>
              {data.skills.map((s,i)=><div key={i}>• {s}</div>)}
            </>)}
          </div>
          <div>
            {data.languages?.length > 0 && (<>
              <AtsHdr>LANGUAGES</AtsHdr>
              {data.languages.map((s,i)=><div key={i}>• {s}</div>)}
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}
function AtsHdr({ children }) {
  return (
    <div className="mt-3">
      <div className="font-bold text-[11px] tracking-wider">{children}</div>
      <div className="h-[0.5px] bg-black mt-0.5"/>
    </div>
  );
}

function AtsCleanPreview({ data }) {
  const p = data.personal || {};
  const contact = [p.email, p.phone, p.website, p.location, p.linkedin].filter(Boolean).join(" | ");
  return (
    <div className="rounded-md border border-[#E2DBCE] bg-white p-5 text-[10.5px] leading-4 text-black min-h-[600px]"
      data-testid="cv-preview-ats_clean" style={{fontFamily:"Plus Jakarta Sans"}}>
      <div className="text-2xl font-bold text-center">{p.full_name || "Your Name"}</div>
      {p.title && <div className="text-[#333] text-[11px] text-center mt-0.5">{p.title}</div>}
      {contact && <div className="text-[10px] text-[#333] text-center mt-1">{contact}</div>}
      {data.summary && (<><CleanHdr>PROFILE</CleanHdr><p>{data.summary}</p></>)}
      {data.experience?.length > 0 && (<>
        <CleanHdr>EXPERIENCE</CleanHdr>
        {data.experience.map((e,i)=>(
          <div key={i} className="mt-1.5">
            <div className="font-semibold">{e.role || "Role"}{e.company && `, ${e.company}`}</div>
            <div className="italic text-[#666] text-[10px]">{[(e.start||e.end) ? `${e.start||""} – ${e.end||"Present"}` : "", e.location].filter(Boolean).join(" | ")}</div>
            {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi} className="ml-3">• {b}</div>)}
          </div>
        ))}
      </>)}
      {data.education?.length > 0 && (<>
        <CleanHdr>EDUCATION</CleanHdr>
        {data.education.map((e,i)=>(
          <div key={i} className="mt-1">
            <div className="font-semibold">{e.degree}{e.field && ` in ${e.field}`}{e.institution && `, ${e.institution}`}</div>
            <div className="italic text-[#666] text-[10px]">{[(e.start||e.end) ? `${e.start||""} – ${e.end||""}` : "", e.location].filter(Boolean).join(" | ")}</div>
          </div>
        ))}
      </>)}
      {data.skills?.length > 0 && (<><CleanHdr>SKILLS</CleanHdr><p>{data.skills.join(", ")}</p></>)}
      {data.languages?.length > 0 && (<><CleanHdr>LANGUAGES</CleanHdr><p>{data.languages.join(", ")}</p></>)}
    </div>
  );
}
function CleanHdr({ children }) {
  return (
    <div className="mt-3">
      <div className="font-bold text-[11px] tracking-wider">{children}</div>
      <div className="h-[0.6px] bg-black mt-0.5"/>
    </div>
  );
}

function ClassicPreview({ data }) {
  const p = data.personal || {};
  const contact = [p.email, p.phone, p.location, p.website].filter(Boolean).join(" · ");
  return (
    <div className="rounded-md border border-[#E2DBCE] bg-white p-4 text-[11px] leading-5 text-[#334155] min-h-[600px]" data-testid="cv-preview-classic">
      <div className="border-b border-[#0F172A] pb-2 mb-2 text-center">
        <div className="font-serif-display font-bold text-lg text-[#0F172A]">{p.full_name || "Your Name"}</div>
        {p.title && <div className="font-serif-display italic text-[#475569]">{p.title}</div>}
        {contact && <div className="text-[10px] text-[#64748B] mt-1">{contact}</div>}
      </div>
      {data.summary && (<><SectionTitle color="#881337">PROFESSIONAL SUMMARY</SectionTitle><p>{data.summary}</p></>)}
      {data.experience?.length>0 && (
        <>
          <SectionTitle color="#881337">EXPERIENCE</SectionTitle>
          {data.experience.map((e,i)=>(
            <div key={i} className="mt-1">
              <div><span className="font-semibold text-[#0F172A]">{e.role || "Role"}</span>{e.company && ` — ${e.company}`}</div>
              <div className="italic text-[#64748B] text-[10px]">{[e.location, (e.start||e.end) ? `${e.start||""} – ${e.end||"Present"}` : ""].filter(Boolean).join(" · ")}</div>
              {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi}>• {b}</div>)}
            </div>
          ))}
        </>
      )}
      {data.education?.length>0 && (
        <>
          <SectionTitle color="#881337">EDUCATION</SectionTitle>
          {data.education.map((e,i)=>(
            <div key={i} className="mt-1">
              <div><span className="font-semibold text-[#0F172A]">{e.degree}{e.field && ` in ${e.field}`}</span>{e.institution && ` — ${e.institution}`}</div>
              <div className="italic text-[#64748B] text-[10px]">{[e.location, (e.start||e.end) ? `${e.start||""} – ${e.end||""}` : ""].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
        </>
      )}
      {data.skills?.length>0 && (<><SectionTitle color="#881337">SKILLS</SectionTitle><p>{data.skills.join(" · ")}</p></>)}
      {data.languages?.length>0 && (<><SectionTitle color="#881337">LANGUAGES</SectionTitle><p>{data.languages.join(" · ")}</p></>)}
    </div>
  );
}

function ModernPreview({ data }) {
  const p = data.personal || {};
  return (
    <div className="rounded-md overflow-hidden border border-[#E2DBCE] bg-white text-[10px] leading-4 text-[#0F172A] min-h-[600px]" data-testid="cv-preview-modern">
      <div className="relative bg-[#1E3A8A] text-white px-4 py-4">
        <div className="text-lg font-bold leading-tight" style={{fontFamily:"Plus Jakarta Sans"}}>{p.full_name || "Your Name"}</div>
        <div className="text-[11px] text-[#DBEAFE]">{p.title || "Job title"}</div>
        {p.photo && (
          <img src={p.photo} alt="" className="absolute top-3 right-3 w-14 h-14 rounded-full object-cover border-2 border-white"/>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#0EA5E9]"/>
      </div>
      <div className="grid grid-cols-[1.6fr_1fr] gap-3 p-4">
        <div>
          {data.summary && <><Bar color="#1E3A8A">PROFILE</Bar><p className="mt-1">{data.summary}</p></>}
          {data.experience?.length>0 && (<>
            <Bar color="#1E3A8A">EXPERIENCE</Bar>
            {data.experience.map((e,i)=>(
              <div key={i} className="mt-1.5">
                <div><span className="font-semibold">{e.role || "Role"}</span>{e.company && <span className="text-[#1E3A8A]"> · {e.company}</span>}</div>
                <div className="italic text-[#64748B]">{[e.location, (e.start||e.end) ? `${e.start||""} – ${e.end||"Present"}` : ""].filter(Boolean).join(" · ")}</div>
                {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi} className="ml-2">• {b}</div>)}
              </div>
            ))}
          </>)}
          {data.education?.length>0 && (<>
            <Bar color="#1E3A8A">EDUCATION</Bar>
            {data.education.map((e,i)=>(
              <div key={i} className="mt-1">
                <div className="font-semibold">{e.degree}{e.field && ` in ${e.field}`}</div>
                <div>{e.institution}</div>
                <div className="italic text-[#64748B]">{[e.location, (e.start||e.end) ? `${e.start||""} – ${e.end||""}` : ""].filter(Boolean).join(" · ")}</div>
              </div>
            ))}
          </>)}
        </div>
        <div>
          <Bar color="#1E3A8A">CONTACT</Bar>
          <div className="space-y-0.5 mt-1">
            {[p.email, p.phone, p.location, p.website, p.linkedin].filter(Boolean).map((v,i)=><div key={i}>{v}</div>)}
          </div>
          {data.skills?.length>0 && (<>
            <Bar color="#1E3A8A">SKILLS</Bar>
            <div className="mt-1">{data.skills.map((s,i)=><div key={i}>• {s}</div>)}</div>
          </>)}
          {data.languages?.length>0 && (<>
            <Bar color="#1E3A8A">LANGUAGES</Bar>
            <div className="mt-1">{data.languages.map((s,i)=><div key={i}>• {s}</div>)}</div>
          </>)}
        </div>
      </div>
    </div>
  );
}

function MinimalPreview({ data }) {
  const p = data.personal || {};
  const contact = [p.email, p.phone, p.location, p.website, p.linkedin].filter(Boolean).join(" | ");
  return (
    <div className="rounded-md border border-[#E2DBCE] bg-white p-4 text-[10.5px] leading-4 text-[#111] min-h-[600px]" data-testid="cv-preview-minimal" style={{fontFamily:"Plus Jakarta Sans"}}>
      <div className="text-2xl font-bold leading-tight">{p.full_name || "Your Name"}</div>
      {p.title && <div className="text-[#555] text-[11px]">{p.title}</div>}
      {contact && <div className="text-[10px] text-[#555] mt-1">{contact}</div>}
      <div className="border-t border-black mt-2"/>
      {data.summary && (<><MinimalH>SUMMARY</MinimalH><p>{data.summary}</p></>)}
      {data.experience?.length>0 && (<>
        <MinimalH>EXPERIENCE</MinimalH>
        {data.experience.map((e,i)=>(
          <div key={i} className="mt-1">
            <div className="font-semibold">{e.role || "Role"}{e.company && `, ${e.company}`}</div>
            <div className="text-[#555] text-[10px]">{[(e.start||e.end) ? `${e.start||""} – ${e.end||"Present"}` : "", e.location].filter(Boolean).join(" | ")}</div>
            {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi} className="ml-2">• {b}</div>)}
          </div>
        ))}
      </>)}
      {data.education?.length>0 && (<>
        <MinimalH>EDUCATION</MinimalH>
        {data.education.map((e,i)=>(
          <div key={i} className="mt-1">
            <div className="font-semibold">{e.degree}{e.field && ` in ${e.field}`}{e.institution && `, ${e.institution}`}</div>
            <div className="text-[#555] text-[10px]">{[(e.start||e.end) ? `${e.start||""} – ${e.end||""}` : "", e.location].filter(Boolean).join(" | ")}</div>
          </div>
        ))}
      </>)}
      {data.skills?.length>0 && (<><MinimalH>SKILLS</MinimalH><p>{data.skills.join(", ")}</p></>)}
      {data.languages?.length>0 && (<><MinimalH>LANGUAGES</MinimalH><p>{data.languages.join(", ")}</p></>)}
    </div>
  );
}

function DarkPreview({ data }) {
  const p = data.personal || {};
  return (
    <div className="rounded-md overflow-hidden border border-[#E2DBCE] flex text-[10px] leading-4 min-h-[600px]" data-testid="cv-preview-dark">
      <aside className="bg-[#0F172A] text-white p-3 w-[38%] relative">
        <div className="absolute right-0 top-0 bottom-0 w-[3px] bg-[#F59E0B]"/>
        {p.photo && <img src={p.photo} alt="" className="w-16 h-16 rounded-full object-cover mx-auto mb-3 border-2 border-white/30"/>}
        <div className="font-bold text-[13px] leading-tight" style={{fontFamily:"Plus Jakarta Sans"}}>{p.full_name || "Your Name"}</div>
        {p.title && <div className="italic text-[#F59E0B] text-[10px]">{p.title}</div>}
        <DarkH>CONTACT</DarkH>
        <div className="text-[#E5E7EB] space-y-0.5">
          {[p.email, p.phone, p.location, p.website, p.linkedin].filter(Boolean).map((v,i)=><div key={i}>{v}</div>)}
        </div>
        {data.skills?.length>0 && (<><DarkH>SKILLS</DarkH>
          <div className="text-[#E5E7EB]">{data.skills.map((s,i)=><div key={i}>› {s}</div>)}</div></>)}
        {data.languages?.length>0 && (<><DarkH>LANGUAGES</DarkH>
          <div className="text-[#E5E7EB]">{data.languages.map((s,i)=><div key={i}>› {s}</div>)}</div></>)}
      </aside>
      <main className="bg-white p-4 flex-1 text-[#111827]">
        {data.summary && (<><DarkMainH>PROFILE</DarkMainH><p>{data.summary}</p></>)}
        {data.experience?.length>0 && (<>
          <DarkMainH>EXPERIENCE</DarkMainH>
          {data.experience.map((e,i)=>(
            <div key={i} className="mt-1.5">
              <div className="font-semibold">{e.role || "Role"}</div>
              <div className="text-[#6B7280] italic text-[10px]">
                <span className="text-[#F59E0B]">● </span>
                {[e.company, (e.start||e.end) ? `${e.start||""} – ${e.end||"Present"}` : "", e.location].filter(Boolean).join(" · ")}
              </div>
              {(e.bullets||[]).filter(Boolean).map((b,bi)=><div key={bi} className="ml-2">• {b}</div>)}
            </div>
          ))}
        </>)}
        {data.education?.length>0 && (<>
          <DarkMainH>EDUCATION</DarkMainH>
          {data.education.map((e,i)=>(
            <div key={i} className="mt-1">
              <div className="font-semibold">{e.degree}{e.field && ` in ${e.field}`}</div>
              <div className="text-[#6B7280] italic text-[10px]">{[e.institution, (e.start||e.end) ? `${e.start||""} – ${e.end||""}` : "", e.location].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
        </>)}
      </main>
    </div>
  );
}

function SectionTitle({ children, color }) {
  return <div className="font-serif-display font-bold text-[11px] tracking-wide mt-2" style={{color}}>{children}</div>;
}
function Bar({ children, color }) {
  return (
    <div className="mt-2">
      <div className="font-bold text-[10px] tracking-wider" style={{color}}>{children}</div>
      <div className="h-[1px] mt-0.5" style={{background:color, opacity:0.4}}/>
    </div>
  );
}
function MinimalH({ children }) {
  return (
    <div className="mt-2">
      <div className="font-bold text-[10px] tracking-wider">{children}</div>
      <div className="h-[1px] bg-[#CCCCCC]"/>
    </div>
  );
}
function DarkH({ children }) {
  return <div className="font-bold text-[10px] tracking-wider text-[#F59E0B] mt-2">{children}</div>;
}
function DarkMainH({ children }) {
  return (
    <div className="mt-2">
      <div className="font-bold text-[11px] tracking-wider">{children}</div>
      <div className="w-8 h-[2px] bg-[#F59E0B] mt-0.5"/>
    </div>
  );
}
